import { useCallback, useRef } from "react";
import { useBulkUpload } from "@/contexts/uploads/BulkUploadContext";
import { getCompactMethods } from "@/hooks/uploads/uploadMethodsConfig";
import { useAudioRecording } from "@/hooks/uploads/useAudioRecording";
import { useUploadFormState } from "@/hooks/uploads/useUploadFormState";
import { cn } from "@/lib/utils";
import {
  UploadAudio,
  UploadCloudStorage,
  UploadDeepResearch,
  UploadDocument,
  UploadImage,
  UploadNotes,
  UploadPdf,
  UploadQuickResearch,
  UploadUrl,
  UploadVideo,
  UploadWebScraping,
  UploadWebScrapingBrightData,
  UploadWebsite,
  UploadWebsiteBrightData,
  UploadYoutube,
} from "./upload";
import { DeferredDocumentAction } from "@/types/knowledge/deferred-operations";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface CompactUploadSectionProps {
  onUploadSuccess?: () => void;
  onDeferredActionAdd?: (action: DeferredDocumentAction) => void;
}

const CompactUploadSection = ({ onUploadSuccess, onDeferredActionAdd }: CompactUploadSectionProps) => {
  const { setAgent } = useBulkUpload();

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const formState = useUploadFormState("none");
  const {
    state,
    setActiveMethod,
    setPdfUrl,
    setPdfFile,
    setYoutubeUrl,
    setWebsiteUrl,
    setScrapeUrl,
    setAudioFile,
    setDocumentFile,
    setImageFiles,
    setVideoFile,
    setVideoUrl,
    setResearchQuery,
    setResearchTitle,
    setSelectedAgentId,
  } = formState;

  const autoResizeTextarea = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, []);

  const { isRecording, isTranscribing, startRecording, stopRecording } = useAudioRecording((transcription) => {
    setResearchQuery(transcription);
    setTimeout(autoResizeTextarea, 0);
  });

  const handleAgentSelect = useCallback(
    (agentId: string) => {
      setSelectedAgentId(agentId);
      if (agentId === "none") {
        setAgent({ id: undefined, name: undefined });
      }
    },
    [setSelectedAgentId, setAgent],
  );

  const uploadMethodsMap = {
    pdf: (
      <UploadPdf
        availableAgents={[]}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        pdfFile={state.pdfFile}
        onPdfFileChange={setPdfFile}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={onDeferredActionAdd}
      />
    ),
    url: (
      <UploadUrl
        availableAgents={[]}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        pdfUrl={state.pdfUrl}
        onPdfUrlChange={setPdfUrl}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={onDeferredActionAdd}
      />
    ),
    youtube: (
      <UploadYoutube
        availableAgents={[]}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        youtubeUrl={state.youtubeUrl}
        onYoutubeUrlChange={setYoutubeUrl}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={onDeferredActionAdd}
      />
    ),
    website: (
      <UploadWebsite
        availableAgents={[]}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        websiteUrl={state.websiteUrl}
        onWebsiteUrlChange={setWebsiteUrl}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={onDeferredActionAdd}
      />
    ),
    "website-brightdata": (
      <UploadWebsiteBrightData
        availableAgents={[]}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        websiteUrl={state.websiteUrl}
        onWebsiteUrlChange={setWebsiteUrl}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={onDeferredActionAdd}
      />
    ),
    audio: (
      <UploadAudio
        availableAgents={[]}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        audioFile={state.audioFile}
        onAudioFileChange={setAudioFile}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={onDeferredActionAdd}
      />
    ),
    document: (
      <UploadDocument
        availableAgents={[]}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        documentFile={state.documentFile}
        onDocumentFileChange={setDocumentFile}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={onDeferredActionAdd}
      />
    ),
    image: (
      <UploadImage
        availableAgents={[]}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        imageFiles={state.imageFiles}
        onImageFilesChange={setImageFiles}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={onDeferredActionAdd}
      />
    ),
    video: (
      <UploadVideo
        availableAgents={[]}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        videoFile={state.videoFile}
        onVideoFileChange={setVideoFile}
        videoUrl={state.videoUrl}
        onVideoUrlChange={setVideoUrl}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={onDeferredActionAdd}
      />
    ),
    notes: (
      <UploadNotes
        availableAgents={[]}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        researchQuery={state.researchQuery}
        researchTitle={state.researchTitle}
        onResearchQueryChange={setResearchQuery}
        onResearchTitleChange={setResearchTitle}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={onDeferredActionAdd}
      />
    ),
    "web-scraping": (
      <UploadWebScraping
        availableAgents={[]}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        scrapeUrl={state.scrapeUrl}
        onScrapeUrlChange={setScrapeUrl}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={onDeferredActionAdd}
      />
    ),
    "website-crawl": (
      <UploadWebScrapingBrightData
        availableAgents={[]}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        scrapeUrl={state.scrapeUrl}
        onScrapeUrlChange={setScrapeUrl}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={onDeferredActionAdd}
      />
    ),
    "quick-research": (
      <UploadQuickResearch
        availableAgents={[]}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        researchQuery={state.researchQuery}
        researchTitle={state.researchTitle}
        textareaRef={textareaRef}
        isRecording={isRecording}
        isTranscribing={isTranscribing}
        onResearchQueryChange={setResearchQuery}
        onResearchTitleChange={setResearchTitle}
        onStartRecording={startRecording}
        onStopRecording={stopRecording}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={onDeferredActionAdd}
      />
    ),
    "deep-research": (
      <UploadDeepResearch
        availableAgents={[]}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        researchQuery={state.researchQuery}
        researchTitle={state.researchTitle}
        textareaRef={textareaRef}
        isRecording={isRecording}
        isTranscribing={isTranscribing}
        onResearchQueryChange={setResearchQuery}
        onResearchTitleChange={setResearchTitle}
        onStartRecording={startRecording}
        onStopRecording={stopRecording}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={onDeferredActionAdd}
      />
    ),
    "cloud-storage": (
      <UploadCloudStorage
        availableAgents={[]}
        selectedAgentId={state.selectedAgentId}
        onAgentSelect={handleAgentSelect}
        onUploadSuccess={onUploadSuccess}
        onDeferredActionAdd={onDeferredActionAdd}
      />
    ),
  };

  const uploadMethods = getCompactMethods();

  return (
    <div>
      {/* Upload Methods Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3 mb-4">
        {uploadMethods.map((method) => (
          <button
            key={method.id}
            onClick={() => !method.comingSoon && setActiveMethod(method.id)}
            className={cn(
              "p-2 sm:p-3 rounded-lg sm:rounded-xl border-2 transition-all duration-300 flex flex-col items-center text-center h-full relative group",
              state.activeMethod === method.id
                ? "border-genie-primary bg-gradient-to-br from-genie-primary/10 to-genie-secondary/5 shadow-lg scale-105"
                : "border-gray-200 hover:border-blue-300 hover:shadow-md hover:scale-105 bg-white",
              method.comingSoon && "opacity-75 cursor-not-allowed hover:scale-100",
            )}
            style={
              state.activeMethod === method.id
                ? {
                    boxShadow:
                      "0 6px 10px -3px rgba(79, 156, 255, 0.2), 0 4px 6px -2px rgba(79, 156, 255, 0.1)",
                  }
                : undefined
            }
            disabled={method.comingSoon}
          >
            {method.comingSoon && (
              <div className="absolute top-1 right-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[8px] sm:text-[9px] font-bold px-1.5 sm:px-2 py-0.5 rounded-full shadow-md">
                Soon
              </div>
            )}
            <GenieIcon
              name={method.genieIcon}
              size={28}
              color={state.activeMethod === method.id ? "#4E9CFF" : "#9CA3AF"}
              gradientEnabled={state.activeMethod === method.id}
              className="mb-1 sm:mb-2 transition-transform group-hover:scale-110"
            />
            <h3
              className={cn(
                "font-semibold text-[10px] sm:text-xs mb-0.5 transition-colors leading-tight",
                state.activeMethod === method.id
                  ? "text-genie-primary"
                  : "text-gray-700 group-hover:text-blue-600",
              )}
            >
              {method.label}
            </h3>
            <p className="text-[8px] sm:text-[10px] text-gray-500 leading-tight hidden sm:block">
              {method.description}
            </p>
          </button>
        ))}
      </div>

      {/* Upload Form Area */}
      <div className="bg-white rounded-xl border-2 border-gray-200 p-6 min-h-[240px] shadow-md">
        {uploadMethodsMap[state.activeMethod as keyof typeof uploadMethodsMap] || null}
      </div>
    </div>
  );
};

export default CompactUploadSection;
